import { Box, DialogContent, Paper } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import {
  backgroundGradients,
  borderRadius,
  palette,
  spaces,
} from '../../../../assets/styles/themes/tokens';
import { TEXT_EDITOR_WIDTH } from '../constants';
import AIAssistantTextEditorContent from './AIAssistantTextEditorContent';
import SwipeableDrawer from '../../../SwipeableDrawer/SwipeableDrawer';
import useScreenSize from '../../../../utils/useScreenSize';
import AIAssistantEditorTopBar from './AIAssistantEditorTopBar';
import AIAssistantTextEditorMobileHeader from './AIAssistantTextEditorMobileHeader';
import AIAssistantTextEditorFloatingToolbar from './AIAssistantTextEditorFloatingToolbar';
import { useSelector } from 'react-redux';
import {
  getCurrentConversationId,
} from '../../../../reducers/aiAssistant.selectors';
import { debounce } from 'debounce';
import services from '../../../../services';
import {
  aiAssistantEditorContext,
  AIAssistantEditorMode,
} from '../../../../state/AIAssistantEditorProvider';
import PodcastDetails from '../../Page/PodcastDetails';
import useAction from '../../../../utils/useAction';
import { updateGeneratedDocument as updateGeneratedDocumentAction } from '../../../../actions/aiAssistant.actions';
import { htmlToMarkdown } from '../../../utils/contentConverter';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../state/AIAssistantContextProvider';
import useRouter from '../../../../utils/useRouter';
import { getAIAssistantPageSource } from '../../../../utils/getAIAssistantPageSource';

const richTextSaveTimeoutMs = 2000;

const getPaperStyles = (isPageMode: boolean) => {
  const commonStyles = {
    borderBottomRightRadius: '0 !important',
    borderTopRightRadius: '0 !important',
    background: backgroundGradients.aiAssistant,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRightColor: palette.primary.contrast,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
  };

  return isPageMode ? {
    ...commonStyles,
    width: '100%',
  } : {
    ...commonStyles,
    width: `${TEXT_EDITOR_WIDTH}px`,
    gridColumn: '1 / 2',
  };
};
const getDialogContentStyles = (isPageMode: boolean, isPodcast: boolean) => ({
  flex: 1,
  padding: 0,
  backgroundColor: isPodcast
    ? palette.background.paperElevation0
    : isPageMode ? palette.background.paperElevation2 : palette.background.paperElevation1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  ...(isPageMode && {
    gap: spaces.lg,
    margin: spaces.md,
    borderRadius: `${borderRadius.sm} ${borderRadius.sm} 0 0`,
    border: `1px solid ${palette.elevation.outlined}`,
  }),
});

const AIAssistantTextEditorContainer = () => {
  const { 
    isTextEditorOpen,
    openTextEditor,
    closeTextEditor,
    editorData,
    setTextEditorContent,
    messageId,
    documentId,
    mode,
    markContentChanged,
  } = useContext(aiAssistantEditorContext);

  const currentContent = useRef(editorData.content);
  const conversationId = useSelector(getCurrentConversationId);
  const isTextEditedRef = useRef(false);
  const updateGeneratedDocument = useAction(updateGeneratedDocumentAction);
  const { match: { location: { pathname } } } = useRouter();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isXsOrSmScreen = isXsScreen || isSmScreen;
  const { displayMode } = useContext(aiAssistantContext);
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
  const isPodcast = mode === AIAssistantEditorMode.PODCAST;

  const handleContentChange = useMemo(
    () => debounce((newContent: string) => {
      if (newContent === currentContent.current) {
        return;
      }
      setTextEditorContent(newContent);
      currentContent.current = newContent;
      if (isXsOrSmScreen) {
        markContentChanged();
      }

      if (mode !== AIAssistantEditorMode.STREAM && !isTextEditedRef.current) {
        isTextEditedRef.current = true;
        const pageSource = getAIAssistantPageSource(pathname);
        services.track.ampliTrackTextEditedEvent(conversationId ?? '', pageSource);
      }

      if (documentId) {
        updateGeneratedDocument({
          documentId: documentId,
          payload: {
            content: htmlToMarkdown(newContent),
          },
        });
      }
    }, richTextSaveTimeoutMs),
    [isXsOrSmScreen,
      markContentChanged,
      mode,
      conversationId,
      documentId,
      updateGeneratedDocument,
      setTextEditorContent
    ]
  );

  useEffect(() => {
    isTextEditedRef.current = false;
  }, []);

  useEffect(() => {
    if (conversationId && mode !== AIAssistantEditorMode.STREAM) {
      isTextEditedRef.current = false;
    }
  }, [conversationId, messageId, documentId, mode]);

  useEffect(() => {
    currentContent.current = editorData.content;
  }, [editorData.content]);

  useEffect(() => {
    return () => {
      handleContentChange.clear?.();
    };
  }, [handleContentChange]);

  if (isXsOrSmScreen) {
    return (
      <SwipeableDrawer
        open={isTextEditorOpen}
        onOpen={openTextEditor}
        onClose={closeTextEditor}
        pullSnap={true}
        initialHeight='full'
        draggable={!isPodcast}
        backgroundColor={isPodcast ? palette.background.paperElevation0 : undefined}
        data-testid='ai-assistant-swipeable-edge'
      >
        {/* The drawer keeps its children mounted while closed; the editor must
            not exist invisibly or a later mode switch unmounts it mid-init. */}
        {isTextEditorOpen && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.xs,
              position: 'relative',
              marginX: `-${spaces.md}`,
            }}
          >
            <AIAssistantTextEditorMobileHeader />
            <Box
              // Remount on panel swap; in-place reconcile crashes on tippy DOM cleanup.
              key={isPodcast ? 'podcast' : 'editor'}
              data-testid='ai-editor-panel-slot'
              sx={{
                flex: 1,
                overflow: 'auto',
              }}
            >
              {isPodcast ? (
                <PodcastDetails />
              ) : (
                <AIAssistantTextEditorContent
                  content={editorData.content}
                  onContentChange={handleContentChange}
                />
              )}
            </Box>
            {!isPodcast && <AIAssistantTextEditorFloatingToolbar />}
          </Box>
        )}
      </SwipeableDrawer>
    );
  }

  return (
    <Paper
      data-testid='ai-assistant-text-editor-container'
      sx={getPaperStyles(isPageMode)}
    >
      <AIAssistantEditorTopBar />
      <DialogContent
        // Remount on panel swap; in-place reconcile crashes on tippy DOM cleanup.
        key={isPodcast ? 'podcast' : 'editor'}
        data-testid='ai-editor-panel-slot'
        sx={getDialogContentStyles(isPageMode, isPodcast)}
      >
        {isPodcast ? (
          <PodcastDetails />
        ) : (
          <AIAssistantTextEditorContent
            content={editorData.content}
            onContentChange={handleContentChange}
          />
        )}
      </DialogContent>
    </Paper>
  );
};

export default AIAssistantTextEditorContainer;
